import { useState } from 'react';
import dayjs from 'dayjs';
import { ChevronDown, ChevronRight } from 'lucide-react';

import { useCategories } from '@/api/category/use-categories.ts';
import { useCreateTransferRecord } from '@/api/transfer/use-create-transfer-record.ts';
import type { SuggestedReceiptDto } from '@/api/types';
import { SuggestedReceiptItemDtoTypeEnum } from '@/api/types';
import { Badge } from '@/components/ui/badge.tsx';
import { Button } from '@/components/ui/button.tsx';
import { formatPrice } from '@/helpers/formatters.ts';

type ReceiptSuggestionRowProps = {
  transferId: string;
  projectId?: string;
  receipt: SuggestedReceiptDto;
  usedItemNames: Set<string>;
  autoExpand?: boolean;
};

const findSingleSuggestedCategoryUuid = (
  items: { suggestedCategory?: { uuid: string } }[],
): string | undefined => {
  if (!items.length) {
    return undefined;
  }

  const firstUuid = items[0].suggestedCategory?.uuid;

  if (!firstUuid) {
    return undefined;
  }

  return items.every((item) => item.suggestedCategory?.uuid === firstUuid) ? firstUuid : undefined;
};

const ReceiptSuggestionRow = ({
  transferId,
  projectId,
  receipt,
  usedItemNames,
  autoExpand,
}: ReceiptSuggestionRowProps) => {
  const items = receipt.items.filter((item) => item.type === SuggestedReceiptItemDtoTypeEnum.Item);
  const availableItems = items.filter((item) => !usedItemNames.has(item.name));
  const singleCategoryUuid = findSingleSuggestedCategoryUuid(availableItems);

  const [expanded, setExpanded] = useState(!!autoExpand);
  const [selectedUuids, setSelectedUuids] = useState<Set<string>>(
    () => new Set(singleCategoryUuid ? availableItems.map((item) => item.uuid) : []),
  );
  const [categoryId, setCategoryId] = useState(singleCategoryUuid || '');

  const { data: categories } = useCategories(projectId);
  const { mutateAsync: createRecord, isPending } = useCreateTransferRecord(transferId);

  const groups = new Map<
    string,
    { categoryUuid?: string; categoryName?: string; itemNames: string[] }
  >();

  for (const item of availableItems) {
    const key = item.suggestedCategory?.uuid || 'none';
    const existing = groups.get(key);

    if (existing) {
      existing.itemNames.push(item.name);
    } else {
      groups.set(key, {
        categoryUuid: item.suggestedCategory?.uuid,
        categoryName: item.suggestedCategory?.name,
        itemNames: [item.name],
      });
    }
  }

  const allAvailableSelected =
    !!availableItems.length && availableItems.every((item) => selectedUuids.has(item.uuid));

  const toggleItem = (uuid: string) => {
    setSelectedUuids((current) => {
      const next = new Set(current);

      if (next.has(uuid)) {
        next.delete(uuid);
      } else {
        next.add(uuid);
      }

      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedUuids(
      allAvailableSelected ? new Set() : new Set(availableItems.map((item) => item.uuid)),
    );
  };

  const selectGroup = (itemNames: string[], groupCategoryId?: string) => {
    const names = new Set(itemNames);
    setSelectedUuids(
      new Set(availableItems.filter((item) => names.has(item.name)).map((item) => item.uuid)),
    );
    setCategoryId(groupCategoryId || '');
  };

  const handleAssign = () => {
    if (!selectedUuids.size || !categoryId) {
      return;
    }

    createRecord({
      receiptId: receipt.uuid,
      itemUuids: [...selectedUuids],
      categoryId,
    })
      .then(() => {
        setSelectedUuids(new Set());
        setExpanded(false);
      })
      .catch(() => undefined);
  };

  return (
    <div className="border rounded-lg text-xs">
      <button
        type="button"
        className="w-full flex items-center justify-between gap-2 p-3 text-left"
        onClick={() => setExpanded((v) => !v)}
      >
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          {expanded ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
          <span className="font-medium">{receipt.party || receipt.extId}</span>
          <span className="text-muted-foreground">
            {receipt.date && dayjs(receipt.date).format('DD.MM.YYYY')}
          </span>
        </div>
        <span className="shrink-0 font-semibold">{formatPrice(receipt.amount)}</span>
      </button>
      {expanded && (
        <div className="flex flex-col gap-3 p-3 pt-0">
          {groups.size > 1 && (
            <div className="flex flex-wrap gap-1.5">
              {[...groups.values()].map((group, index) => (
                <Badge
                  key={index}
                  variant="outline"
                  className="cursor-pointer"
                  onClick={() => selectGroup(group.itemNames, group.categoryUuid)}
                >
                  {group.categoryName || 'Uncategorized'} ({group.itemNames.length})
                </Badge>
              ))}
            </div>
          )}
          {!!availableItems.length && (
            <label className="flex items-center gap-2 py-0.5 text-muted-foreground">
              <input type="checkbox" checked={allAvailableSelected} onChange={toggleSelectAll} />
              <span>{allAvailableSelected ? 'Clear all' : 'Select all'}</span>
            </label>
          )}
          <div className="flex flex-col gap-1">
            {items.map((item) => {
              const used = usedItemNames.has(item.name);

              return (
                <label
                  key={item.uuid}
                  className={`flex items-center gap-2 py-0.5 ${used ? 'opacity-50' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={selectedUuids.has(item.uuid)}
                    disabled={used}
                    onChange={() => toggleItem(item.uuid)}
                  />
                  <span className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
                    {item.name}
                    {item.suggestedCategory && (
                      <Badge variant="info">{item.suggestedCategory.name}</Badge>
                    )}
                    {used && <span className="text-muted-foreground">(already assigned)</span>}
                  </span>
                  <span className="shrink-0 text-muted-foreground">{formatPrice(item.amount)}</span>
                </label>
              );
            })}
          </div>
          <div className="flex items-center gap-2">
            <select
              className="h-7 rounded-md border border-input bg-input/20 dark:bg-input/30 px-2 text-xs flex-1"
              value={categoryId}
              onChange={(event) => setCategoryId(event.target.value)}
            >
              <option value="">Select category</option>
              {categories?.map((category) => (
                <option key={category.uuid} value={category.uuid}>
                  {category.name}
                </option>
              ))}
            </select>
            <Button
              size="sm"
              disabled={!selectedUuids.size || !categoryId || isPending}
              onClick={handleAssign}
            >
              Assign
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReceiptSuggestionRow;
